import { useCallback, useEffect, useState } from 'react';
import {
  FormControl,
  FormHelperText,
  InputLabel,
  CircularProgress,
  Box,
  Popper,
} from '@material-ui/core';
import Autocomplete from '@material-ui/lab/Autocomplete';
import TextField from '@material-ui/core/TextField';
import { FieldExtensionComponentProps } from '@backstage/plugin-scaffolder-react';
import { useApi } from '@backstage/core-plugin-api';
import { fetchApiRef, configApiRef } from '@backstage/core-plugin-api';

/** GitLab API project response (minimal fields we use) */
interface GitLabProject {
  id: number;
  path_with_namespace: string;
  name?: string;
}

/** Backend response when gitlabAccessRequest.group is set: groups + projects for hierarchical dropdown */
interface GitLabHierarchyResponse {
  rootGroupPath: string;
  groups: Array<{ full_path: string; name?: string }>;
  projects: string[];
}

/** One row in the hierarchical dropdown: either a GROUP (non-selectable) or PROJECT (selectable) */
export type GitLabPickerOption =
  | { type: 'group'; path: string; indent: number; isRoot?: boolean }
  | { type: 'project'; path: string; indent: number };

function isHierarchyResponse(
  data: string[] | GitLabHierarchyResponse,
): data is GitLabHierarchyResponse {
  return (
    typeof data === 'object' &&
    data !== null &&
    !Array.isArray(data) &&
    'rootGroupPath' in data &&
    'groups' in data &&
    'projects' in data
  );
}

/** Build a flat list of GROUP/PROJECT options in tree order (depth-first) for dropdown. */
function buildHierarchyOptions(
  rootPath: string,
  groups: Array<{ full_path: string }>,
  projects: string[],
): GitLabPickerOption[] {
  const result: GitLabPickerOption[] = [];

  result.push({ type: 'group', path: rootPath, indent: 0, isRoot: true });

  function addChildren(parentPath: string, indent: number) {
    const parentDepth = parentPath.split('/').length;
    const childDepth = parentDepth + 1;
    const prefix = `${parentPath}/`;
    const childGroups = groups
      .filter(
        g =>
          g.full_path.startsWith(prefix) &&
          g.full_path.split('/').length === childDepth,
      )
      .sort((a, b) => a.full_path.localeCompare(b.full_path));
    const childProjects = projects
      .filter(
        p => p.startsWith(prefix) && p.split('/').length === childDepth,
      )
      .sort();

    for (const g of childGroups) {
      result.push({ type: 'group', path: g.full_path, indent });
      addChildren(g.full_path, indent + 1);
    }
    for (const p of childProjects) {
      result.push({ type: 'project', path: p, indent });
    }
  }

  addChildren(rootPath, 1);
  return result;
}

function getOptionLabel(opt: GitLabPickerOption | string): string {
  if (typeof opt === 'string') return opt;
  const prefix = opt.type === 'group' ? 'GROUP: ' : 'PROJECT: ';
  return prefix + opt.path;
}

interface GitLabProjectPickerUiOptions {
  searchable?: boolean;
  /** Proxy endpoint for GitLab API (e.g. 'gitlab' => /api/proxy/gitlab). Default: 'gitlab' */
  proxyPath?: string;
  /** Use Backstage backend API instead of proxy (e.g. '/api/gitlab-access-request'). Backend uses integrations.gitlab token. */
  apiPath?: string;
}

const FRONTEND_CACHE_TTL_MS = 2 * 60 * 1000; // 2 min – refetch after this to get backend updates

type FrontendCacheEntry = {
  hierarchyOptions: GitLabPickerOption[];
  flatOptions: string[];
  fetchedAt: number;
};

const frontendCache: Record<string, FrontendCacheEntry> = {};

/**
 * Searchable GitLab project picker. When backend returns groups+projects (group config),
 * dropdown shows a hierarchy: GROUP: &lt;path&gt; (bold root), nested subgroups, PROJECT: &lt;path&gt;.
 * Backend caches and refreshes periodically; frontend uses local cache for instant load.
 */
const DEFAULT_BACKEND_API_PATH = '/api/gitlab-access-request';

export const GitLabProjectPickerField = ({
  onChange,
  rawErrors,
  required,
  formData,
  uiSchema,
}: FieldExtensionComponentProps<string, GitLabProjectPickerUiOptions>) => {
  const fetchApi = useApi(fetchApiRef);
  const config = useApi(configApiRef);
  const apiPath = uiSchema?.['ui:options']?.apiPath ?? DEFAULT_BACKEND_API_PATH;
  const proxyPath = uiSchema?.['ui:options']?.proxyPath ?? 'gitlab';
  const cacheKey = apiPath || proxyPath;
  const initialCache = frontendCache[cacheKey];

  const [flatOptions, setFlatOptions] = useState<string[]>(
    () => initialCache?.flatOptions ?? [],
  );
  const [hierarchyOptions, setHierarchyOptions] = useState<GitLabPickerOption[]>(
    () => initialCache?.hierarchyOptions ?? [],
  );
  const [loading, setLoading] = useState(false);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [inputValue, setInputValue] = useState(formData ?? '');
  const searchable = uiSchema?.['ui:options']?.searchable !== false;

  const isHierarchy = hierarchyOptions.length > 0;

  // When user types (e.g. "server"), show only groups/projects whose path contains that text
  const searchTerm = inputValue.replace(/^(GROUP|PROJECT):\s*/i, '').trim().toLowerCase();
  const filteredHierarchyOptions =
    isHierarchy && searchTerm
      ? hierarchyOptions.filter(opt =>
          opt.path.toLowerCase().includes(searchTerm),
        )
      : hierarchyOptions;

  const filteredFlatOptions =
    !isHierarchy && searchTerm
      ? flatOptions.filter(p => p.toLowerCase().includes(searchTerm))
      : flatOptions;

  const options: GitLabPickerOption[] | string[] = isHierarchy
    ? filteredHierarchyOptions
    : filteredFlatOptions;

  const selectedOption: GitLabPickerOption | null =
    isHierarchy && formData
      ? hierarchyOptions.find(o => o.path === formData) ?? null
      : null;

  const applyCache = useCallback((entry: FrontendCacheEntry) => {
    setHierarchyOptions(entry.hierarchyOptions);
    setFlatOptions(entry.flatOptions);
  }, []);

  const fetchProjects = useCallback(
    async (forceRefresh = false) => {
      const fetchCacheKey = apiPath || proxyPath;
      const cached = frontendCache[fetchCacheKey];
      const now = Date.now();
      const cacheValid =
        cached && now - cached.fetchedAt < FRONTEND_CACHE_TTL_MS;

      if (cached && !forceRefresh) {
        applyCache(cached);
        if (cacheValid) return;
      }

      if (cached && !cacheValid) {
        applyCache(cached);
      } else {
        setLoading(true);
      }
      setLoadError(null);
      try {
        let url: string;
        if (apiPath) {
          const backendBaseUrl = config.getString('backend.baseUrl');
          const path = `${apiPath.replace(/\/$/, '')}/projects`;
          url = `${backendBaseUrl}${path.startsWith('/') ? path : `/${path}`}`;
          const res = await fetchApi.fetch(url);
          const contentType = res.headers.get('content-type') ?? '';
          if (!contentType.includes('application/json')) {
            await res.text();
            throw new Error(
              res.ok
                ? 'Response was not JSON'
                : `Backend returned ${res.status}. Ensure GITLAB_TOKEN is set.`,
            );
          }
          if (!res.ok) {
            const data = await res.json().catch(() => ({}));
            throw new Error(
              (data && typeof data.error === 'string' ? data.error : `Backend returned ${res.status}`),
            );
          }
          const data = await res.json();
          if (isHierarchyResponse(data)) {
            const opts = buildHierarchyOptions(
              data.rootGroupPath,
              data.groups,
              data.projects,
            );
            frontendCache[fetchCacheKey] = {
              hierarchyOptions: opts,
              flatOptions: [],
              fetchedAt: Date.now(),
            };
            setHierarchyOptions(opts);
            setFlatOptions([]);
          } else {
            const list = Array.isArray(data) ? data : [];
            frontendCache[fetchCacheKey] = {
              hierarchyOptions: [],
              flatOptions: list,
              fetchedAt: Date.now(),
            };
            setFlatOptions(list);
            setHierarchyOptions([]);
          }
        } else {
          url = `/api/proxy/${proxyPath}/api/v4/projects?per_page=100&order_by=path&sort=asc`;
          const res = await fetchApi.fetch(url);
          if (!res.ok) throw new Error(`GitLab API: ${res.status}`);
          const data: GitLabProject[] = await res.json();
          const list = data.map(p => p.path_with_namespace);
          frontendCache[fetchCacheKey] = {
            hierarchyOptions: [],
            flatOptions: list,
            fetchedAt: Date.now(),
          };
          setFlatOptions(list);
          setHierarchyOptions([]);
        }
      } catch (e) {
        const message =
          e instanceof Error ? e.message : 'Failed to load GitLab projects';
        // eslint-disable-next-line no-console -- projects list fetch failed
        console.error('GitLab projects fetch failed', e);
        setLoadError(message);
        setFlatOptions([]);
        setHierarchyOptions([]);
      } finally {
        setLoading(false);
      }
    },
    [config, fetchApi, proxyPath, apiPath, applyCache],
  );

  const onInputChange = useCallback((_: unknown, value: string) => {
    setInputValue(value);
  }, []);

  const onOpen = useCallback(() => {
    if (!loading) {
      fetchProjects();
    }
  }, [loading, fetchProjects]);

  useEffect(() => {
    fetchProjects();
  }, [fetchProjects]);

  const handleChange = useCallback(
    (_: unknown, value: GitLabPickerOption | string | null) => {
      if (value === null || value === undefined) {
        setInputValue('');
        onChange('');
        return;
      }
      if (typeof value === 'string') {
        setInputValue(value);
        onChange(value);
        return;
      }
      if (value.type === 'project' || value.type === 'group') {
        setInputValue(getOptionLabel(value));
        onChange(value.path);
      }
    },
    [onChange],
  );

  const error = rawErrors && rawErrors.length > 0 && !formData;

  let helperText: string;
  if (loadError) {
    helperText = `Could not load projects: ${loadError}. Ensure GITLAB_TOKEN is set in the backend.`;
  } else if (isHierarchy) {
    helperText = 'Select a GitLab project or group.';
  } else if (searchable) {
    helperText =
      'Dropdown shows all your GitLab projects. Type a word to filter, then select one.';
  } else {
    helperText = 'Select a GitLab project path.';
  }

  return (
    <FormControl margin="normal" required={required} error={error} fullWidth>
      <InputLabel shrink id="gitlab-project-picker-label">
        Gitlab project/Group
      </InputLabel>
      <Box marginTop={1.5}>
      <Autocomplete<GitLabPickerOption | string, false, false, boolean>
        freeSolo={!isHierarchy}
        options={options}
        value={isHierarchy ? selectedOption : (formData ?? null)}
        inputValue={inputValue}
        onInputChange={onInputChange}
        onOpen={onOpen}
        onChange={handleChange}
        loading={loading}
        openOnFocus
        PopperComponent={(props) => (
          <Popper
            {...props}
            placement="bottom-start"
            modifiers={{
              flip: { enabled: false },
              offset: { offset: '0, 8' },
            }}
          />
        )}
        getOptionLabel={opt =>
          typeof opt === 'string' ? opt : getOptionLabel(opt)
        }
        getOptionSelected={(opt, val) =>
          typeof opt === 'object' &&
          typeof val === 'object' &&
          val !== null &&
          opt.path === val.path
        }
        filterOptions={x => x}
        ListboxProps={{
          style: { maxHeight: '70vh', overflow: 'auto' },
        }}
        renderOption={(opt) => {
          if (typeof opt === 'string') {
            return <span>{opt}</span>;
          }
          const label = getOptionLabel(opt);
          const paddingLeft = 8 + opt.indent * 16;
          return (
            <Box
              component="span"
              style={{
                paddingLeft,
                fontWeight: opt.type === 'group' && opt.isRoot ? 700 : undefined,
              }}
            >
              {label}
            </Box>
          );
        }}
        renderInput={params => (
          <TextField
            {...params}
            placeholder="Click to see projects and groups, or type to search..."
            InputProps={{
              ...params.InputProps,
              endAdornment: (
                <>
                  {loading ? (
                    <CircularProgress color="inherit" size={20} />
                  ) : null}
                  {params.InputProps.endAdornment}
                </>
              ),
            }}
          />
        )}
      />
      </Box>
      <FormHelperText error={Boolean(loadError)}>{helperText}</FormHelperText>
    </FormControl>
  );
};
